/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IOrderDetailsViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core-internal';
import * as React from 'react';
import { brandCodeParser } from '../../../Utilities/brand-code-parser';

export const OrderDetailsOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    createdDate,
    channelReferenceId,
    count
}) => (
    <Node {...orderInformationProps}>
        {salesId}
        {createdDate}
        {channelReferenceId}
        {count}
    </Node>
);

export const OrderDetailsSalesLine: React.FC<{line: ISalesLine; context: ICoreContext}> = ({ line: { salesLineProps, salesLine, buyAgainButton, data }, context }) => {
    const brandCode = brandCodeParser({ description: data?.product?.Description, appContext: context });
    return (
        <Node {...salesLineProps}>
            {brandCode && <div className='msc-cart-line__brand'>{brandCode}</div>}
            {salesLine}
            {buyAgainButton}
        </Node>
    );
};

export const OrderDetailsGroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, address, trackingInfo }) => (
    <Node {...deliveryProps}>
        {address}
        {trackingInfo}
    </Node>
);

export const OrderDetailsGroup: React.FC<{group: IGroup; context:ICoreContext}> = ({ group: { groupProps, delivery, salesLinesProps, salesLines }, context }) => (
    <Node {...groupProps}>
        {delivery && <OrderDetailsGroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>
                        <OrderDetailsSalesLine line={salesLine} context={context} />
                    </React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

export const OrderDetailsGroups: React.FC<{groups: IGroups; context: ICoreContext}> = ({ groups: { groupsProps, groups }, context }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderDetailsGroup key={index} group={group} context={context} />
        ))}
    </Node>
);

export const OrderDetailsOrderSummary: React.FC<IOrderSummary> = ({ orderSummaryProps, heading, subtotal, shipping, tax, totalAmount }) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
    </Node>
);

export const OrderDetailsPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps> = ({
    moduleProps,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    groups,
    context
}) => {
    return (
        <Module {...moduleProps}>
            {alert}
            {loading}
            {orderInfomation && <OrderDetailsOrderInfomation {...orderInfomation} />}
            <div className='ms-order-details__main'>
                {groups && <OrderDetailsGroups groups={groups} context={context} />}
            </div>
            <div className='ms-order-details__side'>
                <div className='ms-order-details__side-left'>
                    {groups && <OrderDetailsGroups groups={groups} context={context} />}
                    {payment && <OrderDetailsPayment {...payment} />}
                </div>
                <div className='ms-order-details__side-right'>
                    {orderSummary && <OrderDetailsOrderSummary {...orderSummary} />}
                </div>
            </div>
        </Module>
    );
};

export default OrderDetailsView;
